<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import learningApiUser from '@src/services/api/learning/user'
import DefineValidation from '@services/validation/DefineValidation'
import Modal from '@components/modal/Modal.vue'
import { useLearningAuthStore } from '@stores/auth'
import { commonUtil } from '@utils'

const router = useRouter()
const showCompleteModal = ref(false)
const showExpiredModal = ref(false)
const { tempAccessToken } = storeToRefs(useLearningAuthStore())
const { resetAuth } = useLearningAuthStore()

const { errors, meta, handleSubmit, handleReset, models } = new DefineValidation((modules) => {
  return {
    pincode: modules.string().required().label('認証コード')
  }
})

const errorMessage = ref('')

const toTop = () => {
  showCompleteModal.value = false
  showExpiredModal.value = false
  router.push({
    name: 'Dashboard'
  })
}

const sendButtonDisabled = computed(() => {
  return !(meta.value.valid && meta.value.dirty)
})

const sendForm = handleSubmit(async (values) => {
  // 先に仮発行されたJWTトークンの有効期限を確認、切れていたらエラーモーダルを表示
  if (!commonUtil.checkTokenExpire(tempAccessToken.value)) {
    showExpiredModal.value = true
    return false
  }

  // 有効期限が切れていなかったらAPIを叩く
  const body = {
    pincode: models.pincode.value,
    headers: {
      tempAuthorization: tempAccessToken.value
    }
  }
  learningApiUser.patchMailAddress(body).then((res) => {
    showCompleteModal.value = true
  }).catch((err) => {
    console.error('patchUserSingup err', err.response)
    if (err.response.data.message === 'Unauthorized') {
      errorMessage.value = '認証コードが違います。'
    } else {
      errorMessage.value = 'エラーが発生しました。'
    }
  })
})
</script>

<template>
  <section>
    <div class="container">
      <h2>メールアドレスの変更（認証コード入力）</h2>
      <p>メールに記載された6桁の数字を⼊⼒し、送信ボタンを押してください。</p>
      <form @submit.prevent="sendForm">
        <div class="c-compBox">
          <div class="p-form-field">
            <label
              for="pincode"
              class="c-input-label"
            ><span class="c-form-required">必須</span>認証コード</label>
            <input
              id="pincode"
              v-model="models.pincode.value"
              type="text"
              class="c-input-text"
              placeholder="6桁のコード"
              autocomplete="off"
              maxlength="6"
            >
            <span
              v-if="errors.pincode"
              class="c-validationError"
            >{{ errors.pincode }}</span>
          </div>

          <!-- APIエラー表示 -->
          <div
            v-if="errorMessage"
            class="p-form-error">
            {{ errorMessage }}
          </div>

          <div class="btnContainer">
            <button
              type="submit"
              class="c-button c-button--medium c-button--yellow"
            >
              送信
            </button>
            <RouterLink
              :to="{name:'MailTop'}"
              class="c-button c-button--medium c-button--border"
            >
              戻る
            </RouterLink>
          </div>
        </div>
      </form>
    </div>

    <Modal :show="showCompleteModal">
      <template #body>
        <div class="l-modal__body">
          <h2 class="l-modal__ttl">
            メールアドレスを変更しました。
          </h2>
          <div class="l-modal__buttons">
            <button
              class="c-modalBtn c-modalBtn--single c-modalBtn--gray"
              type="button"
              @click="toTop"
            >
              閉じる
            </button>
          </div>
        </div>
      </template>
    </Modal>

    <Modal :show="showExpiredModal">
      <template #body>
        <div class="l-modal__body">
          <h2 class="l-modal__ttl">
            認証コードの期限が切れました。<br>最初からやり直してください。
          </h2>
          <div class="l-modal__buttons">
            <button
              class="c-modalBtn c-modalBtn--single c-modalBtn--gray"
              type="button"
              @click="toTop"
            >
              閉じる
            </button>
          </div>
        </div>
      </template>
    </Modal>
  </section>
</template>

<style scoped lang="scss">
.container {
  h2 {
    padding: 30px 0 15px;
    font-size: 26px;
    text-align: left;
    font-weight: bold;
  }
}
.btnContainer {
  margin-top: 20px;
}
</style>
